/**
 * Array · String · Module 표시 번호 — 이름 변경·최대값 계산 (동일 패턴)
 */
import { getDataByLevel } from '@/utils/siteDesignDataFlattener.js';
import { readStringDisplayNo, readStringSeq } from './panelMapFeatureProps.js';

const ENTITY_FIELD = {
  array: 'arrayId',
  string: 'stringId',
  module: 'moduleId'
};

const ENTITY_PREFIX = {
  array: /^Array\s+/i,
  string: /^String\s+/i,
  module: /^Module\s+/i
};

const ENTITY_TYPE_LABEL = {
  array: 'Array',
  string: 'String',
  module: 'Module'
};

/**
 * rename 전송·로컬 반영용 canonical name
 * - "1" → "String 1", "String 8" 유지, "Custom label" 유지
 */
export function normalizeEntityRenameName(entityType, newName) {
  const trimmed = (newName || '').trim();
  if (!trimmed) {
    return trimmed;
  }
  const prefix = ENTITY_PREFIX[entityType];
  if (prefix?.test(trimmed)) {
    return trimmed;
  }
  const label = ENTITY_TYPE_LABEL[entityType];
  const displayId = parseDisplayIdFromName(trimmed);
  if (label && displayId != null) {
    return `${label} ${displayId}`;
  }
  return trimmed;
}

/**
 * 이름 끝 토큰에서 표시 번호 추출
 * - "String 7" → 7, "1" → 1, "North Block 3" → 3
 * - 마지막 공백 뒤가 숫자가 아니면 null ("Custom label")
 */
export function parseDisplayIdFromName(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) {
    return null;
  }
  const lastToken = trimmed.includes(' ')
    ? trimmed.slice(trimmed.lastIndexOf(' ') + 1).trim()
    : trimmed;
  if (!/^\d+$/.test(lastToken)) {
    return null;
  }
  const parsed = parseInt(lastToken, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/** PATCH 본문 — canonical name + display id 필드 */
export function buildEntityRenamePayload(entityType, newName) {
  const trimmed = (newName || '').trim();
  if (!trimmed) {
    return {};
  }
  const normalizedName = normalizeEntityRenameName(entityType, trimmed);
  const payload = { name: normalizedName };
  const field = ENTITY_FIELD[entityType];
  const prefix = ENTITY_PREFIX[entityType];
  if (field && prefix?.test(normalizedName)) {
    payload[field] = normalizedName.replace(prefix, '').trim();
    return payload;
  }
  const fromName = parseDisplayIdFromName(normalizedName);
  if (field && fromName != null) {
    payload[field] = String(fromName);
  }
  return payload;
}

/** 이동 번호 변경용 PATCH — 사용자 정의 이름과 표시 번호를 함께 보존 */
export function buildEntityDisplayUpdatePayload(
  entityType,
  { name = '', displayId = null } = {}
) {
  const payload = {};
  const trimmedName = String(name || '').trim();
  const field = ENTITY_FIELD[entityType];

  if (trimmedName) {
    payload.name = trimmedName;
  }
  if (field && displayId != null && displayId !== '') {
    payload[field] = String(displayId);
  }

  return payload;
}

/** 표시 번호 파싱 — rename 입력 → 숫자 또는 null */
export function parseDisplayIdFromRename(entityType, newName) {
  const payload = buildEntityRenamePayload(entityType, newName);
  const field = ENTITY_FIELD[entityType];
  const raw = field ? payload[field] : null;
  if (raw == null || raw === '') {
    return null;
  }
  const parsed = parseInt(String(raw), 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/** 레코드별 유효 표시 번호 — 이름 끝 숫자 우선, 없으면 id 필드 */
function effectiveDisplayId(record, field) {
  const fromName = parseDisplayIdFromName(record?.name);
  if (fromName != null) {
    return fromName;
  }
  const fromField = parseInt(record?.displayNo ?? record?.[field], 10);
  return !Number.isNaN(fromField) ? fromField : null;
}

function maxDisplayIdFromRecords(records, field) {
  let max = 0;
  for (const record of records || []) {
    const id = effectiveDisplayId(record, field);
    if (id != null && id > max) {
      max = id;
    }
  }
  return max;
}

/** 사이트(섹션) 내 Array 표시 번호 최대값 */
export function calculateMaxArrayDisplayIdFromFlattened(flattenedSiteDesign) {
  if (!flattenedSiteDesign) {
    return 0;
  }
  return maxDisplayIdFromRecords(getDataByLevel(flattenedSiteDesign, 'array'), 'arrayId');
}

/**
 * 다음 Array 표시 번호 — flattened + 트리 표시 번호 중 큰 값 + 1.
 * Vuex getter 캐시에 의존하지 말 것 (트리/flattened는 Vuex state 밖).
 */
export function resolveNextArrayDisplayId({ flattened = null, treeDisplayNumbers = [] } = {}) {
  const fromFlat = calculateMaxArrayDisplayIdFromFlattened(flattened);
  const fromTree =
    Array.isArray(treeDisplayNumbers) && treeDisplayNumbers.length > 0
      ? Math.max(...treeDisplayNumbers)
      : 0;
  const max = Math.max(fromFlat, fromTree);
  return max > 0 ? max + 1 : 1;
}

/** Array 내 String 표시 번호 최대값 */
export function calculateMaxStringIdInArrayFromFlattened(flattenedSiteDesign, arraySeq) {
  if (!flattenedSiteDesign || arraySeq == null) {
    return 0;
  }
  const arrayKey = String(arraySeq);
  const strings = (getDataByLevel(flattenedSiteDesign, 'string') || []).filter(
    s => String(s.parentId) === arrayKey
  );
  return maxDisplayIdFromRecords(strings, 'stringId');
}

/** String 내 Module 표시 번호 최대값 */
export function calculateMaxModuleIdInStringFromFlattened(flattenedSiteDesign, stringSeq) {
  if (!flattenedSiteDesign || stringSeq == null) {
    return 0;
  }
  const stringKey = String(stringSeq);
  const modules = (getDataByLevel(flattenedSiteDesign, 'module') || []).filter(
    m => String(m.parentId) === stringKey
  );
  return maxDisplayIdFromRecords(modules, 'moduleId');
}

/** solarStrings 배열 기준 Array 내 최대 String 표시 번호 */
export function calculateMaxStringIdInArray(solarStrings, arraySeq) {
  if (!Array.isArray(solarStrings) || arraySeq == null) {
    return 0;
  }
  const arrayKey = String(arraySeq);
  const strings = solarStrings.filter(
    s => String(s.solarArraySeq ?? s.solarArrayId ?? '') === arrayKey
  );
  return maxDisplayIdFromRecords(strings, 'stringId');
}

/**
 * flattened + solarStrings 병합 — Array 스코프 String 표시 번호 최대값
 * (둘 중 큰 값; 어느 한쪽만 최신이어도 누락 방지)
 */
export function resolveMaxStringDisplayIdInArray({ flattened, solarStrings, arraySeq }) {
  if (arraySeq == null) {
    return 0;
  }
  const fromFlat = calculateMaxStringIdInArrayFromFlattened(flattened, arraySeq);
  const fromList = calculateMaxStringIdInArray(solarStrings, arraySeq);
  return Math.max(fromFlat, fromList);
}

/** Array 스코프 — 다음 String 표시 번호 (max + 1, 없으면 1) */
export function resolveNextStringDisplayIdInArray({ flattened, solarStrings, arraySeq }) {
  if (arraySeq == null) {
    return 1;
  }
  const max = resolveMaxStringDisplayIdInArray({ flattened, solarStrings, arraySeq });
  return max > 0 ? max + 1 : 1;
}

/**
 * String 생성(그리기·복사) 시 대상 Array seq
 * — 복사는 override, 직접 그리기는 시작 시 lock 우선
 */
export function resolveStringCreateTargetArraySeq({
  solarArraySeqOverride = null,
  lockedStringDrawTargetArraySeq = null,
  stringDrawTargetArraySeq = null,
  treeData,
  focusedNodeId,
  resolveCurrentArraySeq
} = {}) {
  if (solarArraySeqOverride != null) {
    return solarArraySeqOverride;
  }
  if (lockedStringDrawTargetArraySeq != null) {
    return lockedStringDrawTargetArraySeq;
  }
  if (stringDrawTargetArraySeq != null) {
    return stringDrawTargetArraySeq;
  }
  if (typeof resolveCurrentArraySeq === 'function') {
    return resolveCurrentArraySeq(treeData, focusedNodeId);
  }
  return null;
}

/** String 스코프 — 다음 Module 표시 번호 (max + 1, 없으면 1) */
export function resolveNextModuleDisplayIdInString({ flattened, stringSeq }) {
  if (stringSeq == null) {
    return 1;
  }
  const max = calculateMaxModuleIdInStringFromFlattened(flattened, stringSeq);
  return max > 0 ? max + 1 : 1;
}

/** nested siteDesign → solarStrings (각 String에 solarArraySeq/id 부여) */
export function extractSolarStringsWithArrayContext(siteDesign) {
  const strings = [];
  siteDesign?.solarSections?.forEach(section =>
    section?.solarArrays?.forEach(array => {
      const arraySeq = array?.id;
      array?.solarStrings?.forEach(string => {
        strings.push({
          ...string,
          stringId: string.stringId,
          solarArraySeq: arraySeq,
          solarArrayId: arraySeq
        });
      });
    })
  );
  return strings;
}

/** String API·하위 호환 */
export function buildStringRenamePayload(newName) {
  return buildEntityRenamePayload('string', newName);
}

export function parseStringDisplayIdFromRename(newName) {
  return parseDisplayIdFromRename('string', newName);
}

export function buildModuleRenamePayload(newName) {
  return buildEntityRenamePayload('module', newName);
}

export function buildArrayRenamePayload(newName) {
  return buildEntityRenamePayload('array', newName);
}

/** Ctrl+드래그 복사 등 — Array 스코프 String 번호 디버그 (seq + displayId) */
export function logStringNumberingInArrayDebug(
  { flattened, solarStrings, arraySeq, sourceFeature } = {},
  label = 'StringNumbering'
) {
  if (arraySeq == null) {
    console.log(`[${label}] arraySeq=null — numbering skipped`);
    return;
  }
  const arrayKey = String(arraySeq);
  const arrayInfo =
    flattened?.byArray?.get(Number(arraySeq)) ??
    flattened?.byArray?.get(arraySeq);
  const fromFlat = calculateMaxStringIdInArrayFromFlattened(flattened, arraySeq);
  const fromList = calculateMaxStringIdInArray(solarStrings, arraySeq);
  const nextDisplayId = resolveNextStringDisplayIdInArray({
    flattened,
    solarStrings,
    arraySeq
  });
  const stringsInFlattened = (getDataByLevel(flattened, 'string') || [])
    .filter(s => String(s.parentId) === arrayKey)
    .map(s => ({
      stringSeq: s.id,
      stringDisplayId: s.stringId,
      name: s.name
    }));
  const stringsInSolarStrings = (solarStrings || [])
    .filter(s => String(s.solarArraySeq ?? s.solarArrayId ?? '') === arrayKey)
    .map(s => ({
      stringSeq: s.id ?? null,
      stringDisplayId: s.stringId,
      solarArraySeq: s.solarArraySeq ?? s.solarArrayId ?? null
    }));

  const source = sourceFeature
    ? {
        stringSeq:
          (sourceFeature.get ? readStringSeq(sourceFeature) : null) ??
          sourceFeature.stringSeq ??
          sourceFeature.solarStringSeq ??
          null,
        stringDisplayId:
          (sourceFeature.get ? readStringDisplayNo(sourceFeature) : null) ??
          sourceFeature.stringDisplayNo ??
          sourceFeature.solarStringId ??
          null,
        arraySeq:
          sourceFeature.get?.('solarArraySeq') ??
          sourceFeature.get?.('solarArrayId') ??
          sourceFeature.solarArraySeq ??
          sourceFeature.solarArrayId ??
          null,
        name: sourceFeature.get?.('name') ?? sourceFeature.name ?? null
      }
    : null;

  console.group(`[${label}] Array seq=${arraySeq} · arrayDisplayId=${arrayInfo?.arrayId ?? '?'}`);
  if (source) {
    console.log('원본(드래그 source feature)', source);
  }
  console.log('대상 Array', {
    arraySeq: arrayInfo?.id ?? arraySeq,
    arrayDisplayId: arrayInfo?.arrayId ?? null,
    arrayName: arrayInfo?.name ?? null
  });
  console.log('스코프 max → next', {
    maxFromFlattened: fromFlat,
    maxFromSolarStrings: fromList,
    mergedMax: Math.max(fromFlat, fromList),
    nextStringDisplayId: nextDisplayId
  });
  console.log('flattened strings in Array', stringsInFlattened);
  console.log('solarStrings in Array', stringsInSolarStrings);
  console.groupEnd();
}
